import { IDateAngularComp } from '@ag-grid-community/angular';
import { IDateParams } from '@ag-grid-community/core';
import { Component, ViewEncapsulation, ViewChild } from '@angular/core';
import { MatCalendar, MatDatepicker } from '@angular/material/datepicker';
import { TranslateService } from '@app/core/services/translate.service';
import { UserStore } from '@app/core/services/user.store';

/**
 * Custom date selection component used in filters for dates.
 */
@Component({
    selector: 'tt-date-filter-selection',
    templateUrl: './date-filter-selection.component.html',
    styleUrls: ['./date-filter-selection.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class DateFilterSelectionComponent implements IDateAngularComp {
    /**
     * The placeholder for the date filter input.
     */
    public placeholder: string = '';

    /**
     * The date to filter with.
     */
    public value: Date | null = null;

    /**
     * Text to display when filter is disabled, (controlled by parent with types not supported to be edited in floating filter component).
     */
    public text: string = '';

    /**
     * Reference to the date picker component.
     */
    @ViewChild(MatDatepicker) picker?: MatDatepicker<Date>;

    /**
     * The filter parameters provided.
     */
    public params?: IDateParams;

    /**
     * Whether this input field has focus or not, used to know if value should be set or not.
     */
    activeFocus = false;

    constructor(
        private user: UserStore,
        private translate: TranslateService,
    ) {
        this.user.dateFormatChanged.subscribe(async (format) => (this.placeholder = await this.translate.translate(format)));
    }

    /**
     * Updates the filter value and applies the filters.
     */
    public onDateChanged(event: Date | null) {
        this.value = event;
        if (this.value) {
            this.value.setHours(0, 0, 0, 0);
        }

        if (!!this.params && !Object.hasOwn(this.params.filterParams, 'filters')) {
            this.params?.onDateChanged();
        } else {
            // @ts-ignore
            this.params?.onDateChanged({ dateFrom: this.value });
        }
    }

    /**
     * Updates activefocus to true.
     */
    public onFocus() {
        this.activeFocus = true;
    }

    /**
     * Updates activefocus to false.
     */
    public onBlur() {
        this.activeFocus = false;
    }

    /**
     * Formats the date on enter key.
     *
     * @param event the key event.
     */
    public onKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.value = !!this.value ? new Date(this.value) : null;
        }
    }

    agInit(params: IDateParams<any, any>): void {
        this.params = params;
    }

    getDate(): Date | null {
        return this.value;
    }

    setDate(date: Date | null): void {
        if (((!date && !!this.value) || (!this.value && !!date) || date?.toISOString().substring(0, 10) !== this.value?.toISOString().substring(0, 10)) && !this.activeFocus) {
            this.value = date;
        }
    }

    onCalendarOpened() {
        setTimeout(() => {
            const calendar = (this.picker as any)._componentRef.instance._calendar;
            if (!calendar) return;

            this.appendWeekNumbers(calendar, true);

            // Trigger når man bytter måned/år med pilene
            calendar.stateChanges.subscribe(() => {
                setTimeout(() => this.appendWeekNumbers(calendar), 0);
            });

            // Trigger når man bytter mellom dato-visning og måned-valg-visning
            calendar.viewChanged.subscribe(() => {
                setTimeout(() => this.appendWeekNumbers(calendar), 0);
            });
        });
    }

    private getIsoWeek(date: Date): number {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    }

    private ensureWrapperExists(datepickerContent: HTMLElement) {
        let wrapper = document.getElementById('week-number-wrapper');

        if (!wrapper && datepickerContent) {
            wrapper = document.createElement('div');
            wrapper.id = 'week-number-wrapper';

            datepickerContent.parentNode?.insertBefore(wrapper, datepickerContent);

            const weekContent = document.createElement('div');
            weekContent.id = 'week-number-content';

            const table = document.createElement('table');
            table.id = 'week-number-table';

            weekContent.appendChild(table);
            wrapper.appendChild(weekContent);
            wrapper.appendChild(datepickerContent);
        }
        return wrapper;
    }

    private async appendWeekNumbers(calendar: MatCalendar<any>, firstOpened: boolean = false) {
        const dateAdapter = (calendar as any)._dateAdapter;
        const datepickerContent = document.querySelector('.mat-datepicker-content') as HTMLElement;
        if (!datepickerContent) return;

        this.ensureWrapperExists(datepickerContent);

        const table = document.getElementById('week-number-table');
        const weekContent = document.getElementById('week-number-content');
        if (!table || !weekContent) return;

        // 1. Sjekk visning. Hvis ikke 'month', deaktiver kolonnen
        if (calendar.currentView !== 'month') {
            weekContent.classList.add('is-hidden');
            table.innerHTML = '';
            return;
        }

        // 2. Aktiver kolonnen for månedsvisning
        weekContent.classList.remove('is-hidden');

        const calendarRows = document.querySelectorAll('.mat-calendar-body tr');
        let rowsHtml = '';

        calendarRows.forEach((row) => {
            const cells = row.querySelectorAll('.mat-calendar-body-cell');
            let weekNum: number | string = '';
            const rowHeight = (row as HTMLElement).offsetHeight || 40;

            for (let i = 0; i < cells.length; i++) {
                const cellText = (cells[i] as HTMLElement).innerText.trim();
                const dayValue = parseInt(cellText, 10);

                if (!isNaN(dayValue)) {
                    const dateInRow = dateAdapter.createDate(dateAdapter.getYear(calendar.activeDate), dateAdapter.getMonth(calendar.activeDate), dayValue);
                    weekNum = this.getIsoWeek(dateInRow);
                    break;
                }
            }
            rowsHtml += `<tr><td style="height: ${rowHeight}px;">${weekNum}</td></tr>`;
        });

        if (firstOpened) {
            await new Promise<void>((resolve) => setTimeout(() => resolve(), 150));
        }

        // 3. Juster posisjon basert på header-høyde
        const calendarBody = document.querySelector('.mat-calendar-body');
        if (calendarBody) {
            const bodyTop = calendarBody.getBoundingClientRect().top;
            const contentTop = datepickerContent.getBoundingClientRect().top;
            weekContent.style.paddingTop = `${bodyTop - contentTop}px`;
        }

        table.innerHTML = rowsHtml;
    }
}
